import { faStethoscope, faTag, faUserSlash } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Alert, Skeleton, Table, TableColumnType, Tag, Typography } from "antd"
import { useContext } from "react"
import VSpace from "../../common/VSpace"
import TenantsContext from "../TenantsContext"
import { CrewMemberUserLinkIssue, LaborSkuIssue } from "../tenantDiagnosticsApi"
import { countTenantDiagnosticsIssues, describeCrewMemberUserLinkReason, describeLaborSkuReason, describeLaborSkuSetting } from "../tenantDiagnosticsModel"
import { useTenantDiagnosticsQuery } from "../tenantDiagnosticsQueries"

const { Text, Title } = Typography

const crewMemberColumns: TableColumnType<CrewMemberUserLinkIssue>[] = [
  {
    key: 'crewMemberName',
    title: 'Crew Member',
    dataIndex: 'crewMemberName',
    render: (value, record) => <>
      {value}
      {
        !record.isCrewMemberActive && <Tag style={{ marginLeft: 8 }}>Inactive</Tag>
      }
    </>
  },
  {
    key: 'crewMemberEmail',
    title: 'Crew Member Email',
    dataIndex: 'crewMemberEmail',
    render: value => value ?? <Text type="secondary">Not set</Text>
  },
  {
    key: 'userEmail',
    title: 'Linked User Email',
    dataIndex: 'userEmail'
  },
  {
    key: 'reason',
    title: 'Problem',
    dataIndex: 'reason',
    render: value => <Text type="danger">{describeCrewMemberUserLinkReason(value)}</Text>
  }
]

const laborSkuColumns: TableColumnType<LaborSkuIssue>[] = [
  {
    key: 'setting',
    title: 'Setting',
    dataIndex: 'setting',
    render: value => describeLaborSkuSetting(value)
  },
  {
    key: 'sku',
    title: 'Configured SKU',
    dataIndex: 'sku',
    render: value => value?.trim() ? value : <Text type="secondary">Empty</Text>
  },
  {
    key: 'matchedItemName',
    title: 'Catalog Item',
    dataIndex: 'matchedItemName',
    render: value => value ?? <Text type="secondary">No match</Text>
  },
  {
    key: 'reason',
    title: 'Problem',
    dataIndex: 'reason',
    render: value => <Text type="danger">{describeLaborSkuReason(value)}</Text>
  }
]

const TenantDiagnosticsSection = () => {
  const { state: { tenant } } = useContext(TenantsContext)

  const diagnosticsQuery = useTenantDiagnosticsQuery(tenant.id)
  const diagnostics = diagnosticsQuery.data
  const issueCount = countTenantDiagnosticsIssues(diagnostics)

  return <section>
    <Title level={5}><FontAwesomeIcon icon={faStethoscope} /> Diagnostics</Title>
    <hr />
    {
      diagnosticsQuery.isLoading && <Skeleton active paragraph={{ rows: 3 }} />
    }
    {
      diagnosticsQuery.isError && <Alert
        type="error"
        showIcon
        message="Diagnostics could not be loaded"
        description="Reload the page to try again."
      />
    }
    {
      diagnostics && issueCount === 0 && <Alert
        type="success"
        showIcon
        message="No configuration problems found"
      />
    }
    {
      diagnostics && issueCount > 0 && <VSpace size="large">
        {
          diagnostics.crewMemberUserLinkIssues.length > 0 && <div>
            <Text strong><FontAwesomeIcon icon={faUserSlash} /> Crew members linked to a user account that no longer exists</Text>
            <Table
              rowKey="crewMemberId"
              columns={crewMemberColumns}
              dataSource={diagnostics.crewMemberUserLinkIssues}
              pagination={false}
              size="small"
            />
          </div>
        }
        {
          diagnostics.laborSkuIssues.length > 0 && <div>
            <Text strong><FontAwesomeIcon icon={faTag} /> Labor SKUs that do not point at a usable catalog item</Text>
            <Table
              rowKey="setting"
              columns={laborSkuColumns}
              dataSource={diagnostics.laborSkuIssues}
              pagination={false}
              size="small"
            />
          </div>
        }
      </VSpace>
    }
  </section>
}

export default TenantDiagnosticsSection
